import { getLocaleState, setLocaleState } from '@home/utils/state';
import { getCommonConfig } from '@dbl/services/global';
import type { IDateEventDto } from '@dbl/services/global';
import { DATALAB_DATE_EVENT } from './constants';
import { EntryDashboardPath, TDatalabDateEvent } from './types';
import type { IDateEventUiItem } from './types';
import moment from 'moment/moment';

/**
 * 检查当前事件数据请求是否已被新请求覆盖
 * @param currentGameCode - 当前请求的gameCode
 * @param currentPathType - 当前请求的pathType
 * @returns 如果请求已被覆盖返回true，否则返回false
 */
const isRequestOverridden = (
  currentGameCode: string,
  currentPathType: string,
): boolean => {
  const { gameCode = '', pathType = '' } = (getLocaleState(
    DATALAB_DATE_EVENT,
  ) as TDatalabDateEvent) || { gameCode: '', pathType: '' };
  // 如果说 从全局变量DATALAB_DATE_EVENT中获取到的gameCode, 和当前请求数据的gameCode不相同, 说明当前请求已被新的请求覆盖, 无需再进行数据的处理和存储
  // pathType 同理.
  return currentGameCode !== gameCode || currentPathType !== pathType;
};
// 转换后端返回的 日期事件的格式
const transformDateEventFormat = (
  dateArr: IDateEventDto[],
): IDateEventUiItem[] =>
  dateArr?.map((date: IDateEventDto) => {
    const { start_date, end_date } = date;
    const startDate = moment(start_date);
    const endDate = moment(end_date);
    return {
      ...date,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      onClick: () => [startDate, endDate],
    };
  });

/**
 * 请求 事件时间数据, 目前主要用于日期筛选器的Event快捷选项
 * 将结果存储在 全局变量DATALAB_DATE_EVENT中,
 * 需要获取Event数据时:
 *     const { data: seasonInfo } = getLocaleState(DATALAB_DATE_EVENT)
 * @param gameCode
 * @param pathType
 */
export const fetchDateEvents = (
  gameCode: string,
  pathType: EntryDashboardPath,
) => {
  const state = getLocaleState(DATALAB_DATE_EVENT) as
    | TDatalabDateEvent
    | undefined;
  const {
    gameCode: eventGameCode = '',
    pathType: eventPathType = '',
    error = undefined,
  } = state || {};

  // 调用路径一致, 且 gameCode 相同
  if (pathType === eventPathType && gameCode === eventGameCode) {
    // 如果loading为true, 不需要请求.
    // 如果loading为false, error 为true, 则再次请求.
    // 如果loading为false, error 为 false, 则不需要请求.
    if (!error) return;
  }

  // 说明 路径不一致, 或者 gameCode不同,
  // 那么gameCode存在时要发起请求.
  if (gameCode) {
    setLocaleState(DATALAB_DATE_EVENT, {
      pathType, // 更新成本次请求的pathType
      gameCode, // 更新成本次请求的gameCode
      data: null,
      loading: true,
      error: undefined,
    });

    getCommonConfig(gameCode)
      .then(result => {
          if (isRequestOverridden(gameCode, pathType)) {
            // 如果说 从全局变量DATALAB_DATE_EVENT中获取到的gameCode, 和当前请求数据的gameCode不相同, 说明当前请求已被新的请求覆盖, 无需再进行数据的处理和存储
            // pathType 同理.
            return;
          }

          // 后端有变动，现在只返回日粒度数据，要求前端自己转为周月
          const dateVal = result?.season_info?.day ?? [];
          const data = transformDateEventFormat(dateVal);
          /**
           * 和产品对齐需求如下:
           *     任何粒度下, 事件数据都是日粒度, 不会跟随展示的粒度改变原本的事件时间范围.
           */
          const seasonInfoResult = {
            hour: data,
            date: data,
            week: data,
            month: data,
          };

          // 请求成功, 更新DATALAB_DATE_EVENT存储的数据
          setLocaleState(DATALAB_DATE_EVENT, {
            gameCode,
            data: seasonInfoResult,
            loading: false,
            error: undefined,
            pathType,
          });
      })
      .catch(error => {
        if (isRequestOverridden(gameCode, pathType)) {
          return;
        }
        // 请求失败, 更新DATALAB_DATE_EVENT存储的数据
        setLocaleState(DATALAB_DATE_EVENT, {
          gameCode,
          data: null,
          loading: false,
          error,
          pathType,
        });
      });
  } else {
    // gameCode不存在时, 更新全局变量
    setLocaleState(DATALAB_DATE_EVENT, {
      gameCode,
      data: null,
      loading: false,
      error: undefined,
      pathType,
    });
  }
};
